import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Search, Loader2, Download, FileText, FolderTree, Package, Check, X, DollarSign } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface Category {
  id: string;
  name: string;
}

interface Price {
  price_type: 'contado' | 'credito' | 'mayorista' | 'publico';
  min_quantity: number;
  price: number;
  offer_price?: number | null;
}

interface Product {
  id: string;
  name: string;
  code?: string;
  main_image: string;
  category_id: string;
  prices?: Price[];
  specifications?: { name: string; value: string; order: number; }[];
}

interface BusinessInfo {
  name: string;
  address: string;
  phone: string;
  whatsapp: string;
}

const priceTypeConfig: Record<Price['price_type'], { label: string; pdfLabel: string; color: string; bg: string; ring: string; icon: string }> = {
  publico: { label: 'Público', pdfLabel: 'Público', color: 'text-gray-800', bg: 'bg-gray-100', ring: 'ring-gray-400', icon: '🏷️' },
  contado: { label: 'Contado', pdfLabel: 'Por Mayor Co', color: 'text-green-800', bg: 'bg-green-100', ring: 'ring-green-400', icon: '💵' },
  credito: { label: 'Crédito', pdfLabel: 'Por Mayor Cr', color: 'text-blue-800', bg: 'bg-blue-100', ring: 'ring-blue-400', icon: '📋' },
  mayorista: { label: 'Mayorista', pdfLabel: 'Mayorista', color: 'text-purple-800', bg: 'bg-purple-100', ring: 'ring-purple-400', icon: '📦' },
};

export default function CatalogsTab() {
  const [loadingPerm, setLoadingPerm] = useState(true);
  const [hasCatalogPermission, setHasCatalogPermission] = useState(false);

  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [mode, setMode] = useState<'category' | 'product'>('category');
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [priceType, setPriceType] = useState<Price['price_type']>('publico');
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);
  const liveSearchTimeout = useRef<number | null>(null);

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setHasCatalogPermission(false);
          return;
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin, admin_permissions, price_type')
          .eq('id', user.id)
          .single();

        const isAdmin = profile?.is_admin;
        const hasPerm = profile?.admin_permissions?.catalogs;

        setHasCatalogPermission(Boolean(isAdmin) || Boolean(hasPerm));
        if (profile?.price_type) {
          setPriceType(profile.price_type as Price['price_type']);
        }
      } catch (error) {
        console.error('Error verificando permisos de catálogos:', error);
        setHasCatalogPermission(false);
      } finally {
        setLoadingPerm(false);
      }
    };

    checkPermissions();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (mode !== 'product') return;
    if (liveSearchTimeout.current) {
      clearTimeout(liveSearchTimeout.current);
    }
    liveSearchTimeout.current = window.setTimeout(() => {
      if (productSearch.trim().length === 0) {
        setSearchResults([]);
      } else {
        searchProducts(productSearch.trim());
      }
    }, 400);
    return () => {
      if (liveSearchTimeout.current) {
        clearTimeout(liveSearchTimeout.current);
      }
    };
  }, [productSearch, mode]);

  const fetchCategories = async () => {
    const { data, error } = await supabase.from('categories').select('id, name').order('name');
    if (!error) setCategories(data || []);
  };

  const searchProducts = async (term: string) => {
    const { data, error } = await supabase
      .from('products')
      .select('id, name, code, main_image, category_id, prices:product_prices(price_type, min_quantity, price, offer_price), specifications:product_specifications(name, value, order)')
      .ilike('name', `%${term}%`)
      .limit(10);
    if (!error) setSearchResults(data || []);
  };

  const toggleCategory = (id: string) => {
    setSelectedCategories(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  };

  const selectAllCategories = () => setSelectedCategories(categories.map(c => c.id));
  const clearCategories = () => setSelectedCategories([]);

  const addProduct = (product: Product) => {
    if (!selectedProducts.find(p => p.id === product.id)) {
      setSelectedProducts(prev => [...prev, product]);
    }
    setProductSearch('');
    setSearchResults([]);
  };

  const removeProduct = (id: string) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== id));
  };

  const generateCatalog = async () => {
    try {
      setGeneratingPDF(true);
      setProgressPercent(0);
      setProgressMessage('Cargando productos...');
      let products: Product[] = [];
      const businessInfo: BusinessInfo = {
        name: "Distribuidora del Sol Guatemala S.A.",
        address: "19 calle 0-47 zona 1, Guatemala, Guatemala",
        phone: "2238-0714",
        whatsapp: "4739-1242"
      };
      if (mode === 'category') {
        if (selectedCategories.length === 0) {
          alert('Selecciona al menos una categoría');
          return;
        }
        const { data, error } = await supabase
          .from('products')
          .select('id, name, code, main_image, category_id, prices:product_prices(price_type, min_quantity, price, offer_price), specifications:product_specifications(name, value, order)')
          .in('category_id', selectedCategories)
          .eq('status', 'active');
        if (error) throw error;
        products = data || [];
      } else {
        if (selectedProducts.length === 0) {
          alert('Agrega productos a la lista');
          return;
        }
        const ids = selectedProducts.map(p => p.id);
        const { data, error } = await supabase
          .from('products')
          .select('id, name, code, main_image, category_id, prices:product_prices(price_type, min_quantity, price, offer_price), specifications:product_specifications(name, value, order)')
          .in('id', ids);
        if (error) throw error;
        products = data || [];
      }
      if (products.length === 0) {
        alert('No se encontraron productos para generar el catálogo');
        return;
      }
      setProgressPercent(10);
      setProgressMessage('Generando páginas del catálogo...');
      await buildPDF(products, businessInfo);
      setProgressPercent(100);
      setProgressMessage('Catálogo generado exitosamente');
    } catch (err) {
      console.error(err);
      alert('Ocurrió un error al generar el catálogo');
    } finally {
      setTimeout(() => {
        setGeneratingPDF(false);
        setProgressPercent(0);
        setProgressMessage('');
      }, 1500);
    }
  };

  const buildPDF = async (products: Product[], businessInfo: BusinessInfo) => {
    const toBase64 = (url: string): Promise<string> => {
      return fetch(url)
        .then(resp => resp.blob())
        .then(blob => new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        }));
    };

    const pdf = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    let logoBase64: string | null = null;
    try {
      logoBase64 = await toBase64('/assets/DISOLGUA.png');
    } catch {
      console.warn('No se pudo cargar el logo para el PDF');
    }

    const drawHeader = () => {
      if (!logoBase64) return;
      const headerW = 80;
      const headerH = 30;
      const margin = 10;
      pdf.addImage(logoBase64, 'PNG', margin, margin, headerW, headerH);
      pdf.setFontSize(18);
      const textX = margin + headerW + 5;
      let textY = margin + 5;
      pdf.text(businessInfo.name, textX, textY);
      pdf.setFontSize(9);
      textY += 5;
      pdf.text(businessInfo.address, textX, textY);
      textY += 4;
      pdf.text(`Tel: ${businessInfo.phone}`, textX, textY);
      textY += 4;
      pdf.text(`Whatsapp: ${businessInfo.whatsapp}`, textX, textY);
      textY += 4;
      pdf.text('www.disolgua.com', textX, textY);
      // Price list label
      textY += 5;
      pdf.setFontSize(11);
      pdf.setTextColor(37, 99, 235);
      pdf.text(`Lista de Precios: ${priceTypeConfig[priceType].pdfLabel}`, textX, textY);
      pdf.setTextColor(0, 0, 0);
    };

    const drawFooter = () => {
      pdf.setFontSize(8);
      pdf.setTextColor(120, 120, 120);
      pdf.text('www.disolgua.com', pageWidth / 2, pageHeight - 8, { align: 'center' });
      pdf.setTextColor(0, 0, 0);
    };

    const drawWatermark = () => {
      if (!logoBase64) return;
      const headerW = 40;
      const headerH = 15;
      const wmWidth = pageWidth * 0.6;
      const wmHeight = wmWidth * (headerH / headerW);
      const wmX = (pageWidth - wmWidth) / 2;
      const wmY = (pageHeight - wmHeight) / 2;

      pdf.saveGraphicsState();
      // @ts-ignore
      pdf.setGState(new (pdf as any).GState({ opacity: 0.07 }));
      pdf.addImage(logoBase64, 'PNG', wmX, wmY, wmWidth, wmHeight);
      pdf.restoreGraphicsState();
    };

    drawHeader();

    let y = 50;
    const cols = 2;
    const cellPadding = 4;
    const cellWidth = (pageWidth - 20 - (cols - 1) * cellPadding) / cols;

    let col = 0;
    let rowMaxHeight = 0;

    for (let i = 0; i < products.length; i++) {
      const product = products[i];

      // Update progress
      const pct = 10 + Math.round((i / products.length) * 85);
      setProgressPercent(pct);
      if (i % 5 === 0) {
        setProgressMessage(`Procesando producto ${i + 1} de ${products.length}...`);
      }

      const card = document.createElement('div');
      card.style.display = 'flex';
      card.style.flexDirection = 'column';
      card.style.alignItems = 'center';
      card.style.width = `${cellWidth * 3}px`;
      card.style.padding = '8px';
      card.style.fontFamily = 'sans-serif';
      card.style.border = '1px solid #e5e7eb';

      const img = document.createElement('img');
      img.src = product.main_image;
      img.style.width = '100px';
      img.style.height = '100px';
      img.style.objectFit = 'contain';
      img.crossOrigin = 'anonymous';
      card.appendChild(img);

      const nameEl = document.createElement('div');
      nameEl.style.fontSize = '12px';
      nameEl.style.fontWeight = '600';
      nameEl.style.color = '#111827';
      nameEl.style.textAlign = 'center';
      nameEl.style.marginTop = '4px';
      nameEl.textContent = product.name;
      card.appendChild(nameEl);

      if (product.code) {
        const codeEl = document.createElement('div');
        codeEl.style.fontSize = '10px';
        codeEl.style.color = '#374151';
        codeEl.style.textAlign = 'center';
        codeEl.textContent = `Código: ${product.code}`;
        card.appendChild(codeEl);
      }

      const priceObj = product.prices
        ?.filter(p => p.price_type === priceType)
        .sort((a, b) => a.min_quantity - b.min_quantity)[0];
      if (priceObj) {
        if (priceObj.offer_price != null) {
          const originalEl = document.createElement('div');
          originalEl.style.fontSize = '10px';
          originalEl.style.color = '#374151';
          originalEl.style.textDecoration = 'underline';
          originalEl.style.textAlign = 'center';
          originalEl.textContent = `Q ${priceObj.price}`;
          card.appendChild(originalEl);

          const offerEl = document.createElement('div');
          offerEl.style.fontSize = '12px';
          offerEl.style.fontWeight = '600';
          offerEl.style.color = '#e63946';
          offerEl.style.textAlign = 'center';
          offerEl.textContent = `Oferta: Q ${priceObj.offer_price}`;
          card.appendChild(offerEl);

          const minQtyEl = document.createElement('div');
          minQtyEl.style.fontSize = '10px';
          minQtyEl.style.color = '#374151';
          minQtyEl.style.textAlign = 'center';
          minQtyEl.textContent = `Cant. mínima: ${priceObj.min_quantity}`;
          card.appendChild(minQtyEl);
        } else {
          const priceEl = document.createElement('div');
          priceEl.style.fontSize = '11px';
          priceEl.style.fontWeight = '500';
          priceEl.style.color = '#111827';
          priceEl.style.textAlign = 'center';
          priceEl.textContent = `Precio: Q ${priceObj.price}`;
          card.appendChild(priceEl);

          const minQtyEl = document.createElement('div');
          minQtyEl.style.fontSize = '10px';
          minQtyEl.style.color = '#374151';
          minQtyEl.style.textAlign = 'center';
          minQtyEl.textContent = `Cant. mínima: ${priceObj.min_quantity} unidades`;
          card.appendChild(minQtyEl);
        }
      } else {
        const priceEl = document.createElement('div');
        priceEl.style.fontSize = '11px';
        priceEl.style.fontWeight = '500';
        priceEl.style.color = '#111827';
        priceEl.style.textAlign = 'center';
        priceEl.textContent = `Precio: ---`;
        card.appendChild(priceEl);
      }

      if (product.specifications) {
        product.specifications.forEach(spec => {
          const specEl = document.createElement('div');
          specEl.style.fontSize = '8px';
          specEl.style.color = '#374151';
          specEl.style.textAlign = 'center';
          specEl.textContent = `${spec.name}: ${spec.value}`;
          card.appendChild(specEl);
        });
      }

      document.body.appendChild(card);
      const canvas = await html2canvas(card, { scale: 2, useCORS: true, logging: false });
      document.body.removeChild(card);

      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      const imgHeight = (canvas.height * cellWidth) / canvas.width;

      if (y + imgHeight > pageHeight - 15) {
        drawFooter();
        drawWatermark();
        pdf.addPage();
        drawHeader();
        y = 50;
        col = 0;
        rowMaxHeight = 0;
      }

      const x = 10 + col * (cellWidth + cellPadding);
      pdf.addImage(imgData, 'JPEG', x, y, cellWidth, imgHeight);
      rowMaxHeight = Math.max(rowMaxHeight, imgHeight);

      col++;
      if (col === cols) {
        col = 0;
        y += rowMaxHeight + cellPadding;
        rowMaxHeight = 0;
      }
    }

    drawFooter();
    drawWatermark();
    pdf.save(`catalogo-${priceTypeConfig[priceType].label.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  if (loadingPerm) {
    return (
      <div className="flex items-center justify-center bg-white rounded-xl shadow-sm p-6 h-40">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!hasCatalogPermission) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 text-center text-red-600 font-medium">
        No tienes permisos para acceder al generador de catálogos.
      </div>
    );
  }

  const selectionCount = mode === 'category' ? selectedCategories.length : selectedProducts.length;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          Generador de Catálogos
        </h2>
        <p className="text-sm text-gray-500 mt-1">Selecciona productos y genera un catálogo PDF con precios.</p>
      </div>

      {/* Mode tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setMode('category')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            mode === 'category'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <FolderTree className="w-4 h-4" />
          Por Categorías
        </button>
        <button
          onClick={() => setMode('product')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            mode === 'product'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Package className="w-4 h-4" />
          Por Productos
        </button>
      </div>

      {/* Category selection */}
      {mode === 'category' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">
              Categorías {selectedCategories.length > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                  {selectedCategories.length}
                </span>
              )}
            </span>
            <div className="flex gap-2">
              <button onClick={selectAllCategories} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                Seleccionar todo
              </button>
              <span className="text-gray-300">|</span>
              <button onClick={clearCategories} className="text-xs text-gray-500 hover:text-gray-700 font-medium">
                Limpiar
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto p-1">
            {categories.map(cat => {
              const selected = selectedCategories.includes(cat.id);
              return (
                <button
                  key={cat.id}
                  onClick={() => toggleCategory(cat.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${
                    selected
                      ? 'bg-blue-50 ring-2 ring-blue-500 text-blue-800 font-medium'
                      : 'border border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                  <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${
                    selected ? 'bg-blue-600 text-white' : 'border border-gray-300'
                  }`}>
                    {selected && <Check className="w-3 h-3" />}
                  </span>
                  <span className="truncate">{cat.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Product selection */}
      {mode === 'product' && (
        <div>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre de producto..."
              value={productSearch}
              onChange={e => setProductSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg pl-10 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchResults.length > 0 && (
              <ul className="absolute z-10 bg-white border border-gray-200 rounded-lg w-full mt-1 max-h-60 overflow-y-auto shadow-lg">
                {searchResults.map(res => (
                  <li
                    key={res.id}
                    onClick={() => addProduct(res)}
                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm flex items-center gap-3"
                  >
                    <img src={res.main_image} alt="" className="w-8 h-8 object-contain rounded bg-gray-50 flex-shrink-0" />
                    <div>
                      <div className="text-gray-900">{res.name}</div>
                      {res.code && <div className="text-xs text-gray-500">{res.code}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {selectedProducts.length > 0 && (
            <div className="mt-4">
              <span className="text-sm font-medium text-gray-700">
                Productos seleccionados
                <span className="ml-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">
                  {selectedProducts.length}
                </span>
              </span>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-52 overflow-y-auto p-1">
                {selectedProducts.map(p => (
                  <div key={p.id} className="flex items-center gap-3 border border-gray-200 rounded-lg p-2 bg-gray-50">
                    <img src={p.main_image} alt="" className="w-10 h-10 object-contain rounded bg-white flex-shrink-0" />
                    <span className="text-sm text-gray-800 truncate flex-1">{p.name}</span>
                    <button
                      onClick={() => removeProduct(p.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Price type selector */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <DollarSign className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Lista de precios</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {(Object.keys(priceTypeConfig) as Price['price_type'][]).map(type => {
            const cfg = priceTypeConfig[type];
            const active = priceType === type;
            return (
              <button
                key={type}
                onClick={() => setPriceType(type)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  active
                    ? `ring-2 ring-blue-500 ${cfg.bg} ${cfg.color}`
                    : 'border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span className={`px-1.5 py-0.5 rounded text-xs ${active ? cfg.bg + ' ' + cfg.color : 'bg-gray-100 text-gray-500'}`}>
                  {cfg.icon}
                </span>
                {cfg.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Generate button + progress */}
      <div className="border-t border-gray-100 pt-6">
        <div className="flex items-center gap-4">
          <button
            onClick={generateCatalog}
            disabled={generatingPDF || selectionCount === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg flex items-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {generatingPDF ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Generando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" /> Generar PDF
              </>
            )}
          </button>
          {!generatingPDF && selectionCount > 0 && (
            <span className="text-sm text-gray-500">
              {selectionCount} {mode === 'category' ? 'categoría(s)' : 'producto(s)'} · {priceTypeConfig[priceType].label}
            </span>
          )}
        </div>

        {generatingPDF && (
          <div className="mt-4 space-y-2">
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <p className="text-xs text-gray-500">{progressMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
}
